class ProductSavingsMilestones {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.productId = container.dataset.productId;
    this.pricingData = this.safeJson(container.dataset.metafieldJson, null);
    this.variantPrices = this.safeJson(container.dataset.variantPrices, {});
    this.variantOptions = this.safeJson(container.dataset.variantOptions, {});
    this.currentGroupKey = null;
    this.currentVariantId = this.getSelectedVariantId();
    this.currentQuantity = this.getCurrentQuantity();

    this.nodesWrap = container.querySelector('[data-milestone-nodes]');

    this.init();
  }

  safeJson(value, fallback) {
    try {
      return typeof value === 'string' && value.trim() ? JSON.parse(value) : fallback;
    } catch (error) {
      console.warn('Savings milestones: invalid JSON data.', error);
      return fallback;
    }
  }

  init() {
    if (!this.pricingData || !Array.isArray(this.pricingData.groups)) {
      this.hide();
      return;
    }

    this.bindEvents();
    this.update();
  }

  bindEvents() {
    const scheduleUpdate = () => window.setTimeout(() => this.update(), 0);
    const scheduleSteppedUpdate = () => {
      [0, 50, 150, 300].forEach((delay) => {
        window.setTimeout(() => this.update(), delay);
      });
    };

    document.addEventListener('change', scheduleUpdate);
    document.addEventListener('input', scheduleUpdate);
    document.addEventListener('camo:multi-flavor-quantity-change', scheduleSteppedUpdate);
    document.addEventListener('click', scheduleSteppedUpdate);
    document.addEventListener('keyup', scheduleUpdate);

    this.nodesWrap?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-milestone-quantity-button]');
      if (!button) return;

      event.preventDefault();
      this.setCurrentQuantity(Number(button.dataset.milestoneQuantity));
      scheduleSteppedUpdate();
    });

    ['variant:change', 'product:variant-change', 'theme:variant:change', 'xo:variant:change'].forEach((eventName) => {
      document.addEventListener(eventName, scheduleSteppedUpdate);
    });

    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      scheduleUpdate();
    };
    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      scheduleUpdate();
    };
    window.addEventListener('popstate', scheduleUpdate);

    // Some Shopify quantity components update the input value silently without firing input/change.
    // This lightweight watcher keeps the milestone cards synced without touching the theme quantity code.
    window.setInterval(() => {
      const nextVariantId = this.getSelectedVariantId();
      const nextQuantity = this.getCurrentQuantity();

      if (nextVariantId !== this.currentVariantId || nextQuantity !== this.currentQuantity) {
        this.currentVariantId = nextVariantId;
        this.currentQuantity = nextQuantity;
        this.update();
      }
    }, 150);
  }

  getProductRoot() {
    return this.container.closest('xo-product') || this.container.closest('[data-section-id]') || document;
  }

  getQuantityInput() {
    const root = this.getProductRoot();
    return (
      root.querySelector('input[name="quantity"]') ||
      root.querySelector('.quantity__input') ||
      root.querySelector('[id^="Quantity-"]') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  getSelectedVariantId() {
    const root = this.getProductRoot();
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    const checkedId = root.querySelector('input[name="id"]:checked');
    const fieldId = root.querySelector('select[name="id"], input[name="id"]');
    const id = checkedId?.value || fieldId?.value || urlVariant || this.container.dataset.currentVariantId;
    return parseInt(id, 10);
  }

  getCurrentQuantity() {
    const root = this.getProductRoot();
    const form = root.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"], form');
    const multiFlavorQuantity = parseInt(form?.dataset?.camoMultiFlavorActualQuantity || '', 10);
    if (Number.isFinite(multiFlavorQuantity) && multiFlavorQuantity > 0) return multiFlavorQuantity;

    const quantityInput = this.getQuantityInput();
    const quantity = parseInt(quantityInput?.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  setCurrentQuantity(quantity) {
    const nextQuantity = parseInt(quantity, 10);
    const root = this.getProductRoot();
    const form = root.querySelector('form[action*="/cart/add"], form[data-type="add-to-cart-form"], form');

    if (!Number.isFinite(nextQuantity) || nextQuantity < 1) return;

    // Flavor-variant products own their milestone top-up logic. The milestone buttons
    // should only add the missing amount into the flavor button badges, not change the
    // main xo-cart-quantity multiplier.
    if (typeof form?.camoApplyFlavorMilestone === 'function') {
      form.camoApplyFlavorMilestone(nextQuantity);
      this.currentQuantity = this.getCurrentQuantity();
      this.update();
      return;
    }

    const quantityInput = this.getQuantityInput();
    if (!quantityInput) return;

    quantityInput.value = nextQuantity;
    quantityInput.setAttribute('value', String(nextQuantity));
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    this.currentQuantity = nextQuantity;
    this.update();
  }

  normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/\s+/g, ' ');
  }

  getVariantOptionMap(variantId) {
    const variant = this.variantOptions[String(variantId)];
    const map = {};
    if (!variant) return map;

    const optionNames = Array.isArray(variant.option_names) ? variant.option_names : [];
    optionNames.forEach((name, index) => {
      const key = this.normalize(name).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      map[key] = variant[`option${index + 1}`];
    });

    return map;
  }

  getGroup(variantId) {
    const directMatch = this.pricingData.groups.find((group) => {
      return Array.isArray(group.variant_ids) && group.variant_ids.map(Number).includes(Number(variantId));
    });

    if (directMatch) return directMatch;

    // Fallback for products that add a Flavor option. Their variant IDs are new,
    // but the existing pricing groups still describe the Type + Pack Type pair.
    const optionMap = this.getVariantOptionMap(variantId);
    const selectedType = this.normalize(optionMap.type);
    const selectedPackType = this.normalize(optionMap['pack-type'] || optionMap.pack);

    if (!selectedType && !selectedPackType) return null;

    return this.pricingData.groups.find((group) => {
      const groupType = this.normalize(group.product_type);
      const groupPackType = this.normalize(group.pack_type);
      return (!groupType || groupType === selectedType) && (!groupPackType || groupPackType === selectedPackType);
    }) || null;
  }

  getBasePrice(variantId) {
    const cents = parseFloat(this.variantPrices[String(variantId)] || 0);
    return cents > 0 ? cents / 100 : 0;
  }

  getVisibleBreakpoints(group) {
    return [...group.breakpoints]
      .sort((a, b) => Number(a.quantity) - Number(b.quantity))
      .filter((breakpoint) => Number(breakpoint.discount_percentage) > 0);
  }

  getGroupKey(group) {
    return `${group.product_type || ''}-${group.pack_type || ''}-${(this.getVisibleBreakpoints(group) || []).map((bp) => `${bp.quantity}:${bp.discount_percentage}`).join('|')}`;
  }

  renderNodes(group) {
    this.nodesWrap.innerHTML = '';

    const visibleBreakpoints = this.getVisibleBreakpoints(group);
    this.nodesWrap.style.setProperty('--milestone-count', Math.max(1, visibleBreakpoints.length));

    visibleBreakpoints.forEach((breakpoint) => {
      const node = document.createElement('div');
      node.className = 'milestone-node';
      node.dataset.quantity = breakpoint.quantity;
      node.dataset.discount = breakpoint.discount_percentage;
      node.innerHTML = `
        <button class="milestone-quick-add-button" type="button" data-milestone-quantity-button data-milestone-quantity="${breakpoint.quantity}">
          <span class="node-circle">
            <span class="node-circle-number"></span>
            <span class="node-circle-label"></span>
          </span>
          <span class="node-meta">
            <span class="node-qty"></span>
            <span class="node-pill"></span>
            <span class="node-pouch-price"></span>
            <span class="node-helper"></span>
          </span>
        </button>
      `;
      this.nodesWrap.appendChild(node);
    });
  }

  update() {
    const variantId = this.getSelectedVariantId();
    const group = this.getGroup(variantId);

    if (!group || !Array.isArray(group.breakpoints) || group.breakpoints.length === 0 || this.getVisibleBreakpoints(group).length === 0) {
      this.hide();
      return;
    }

    this.show();

    const groupKey = this.getGroupKey(group);
    if (groupKey !== this.currentGroupKey) {
      this.renderNodes(group);
      this.currentGroupKey = groupKey;
    }

    const quantity = this.getCurrentQuantity();
    this.currentVariantId = variantId;
    this.currentQuantity = quantity;
    const basePrice = this.getBasePrice(variantId);
    const visibleBreakpoints = this.getVisibleBreakpoints(group);
    const pouchCountPerUnit = Number(group.pouch_count_per_unit) || 1;
    const packLabel = String(group.pack_type || 'units').toLowerCase();

    this.nodesWrap.style.setProperty('--milestone-count', Math.max(1, visibleBreakpoints.length));

    const activeTierIndex = visibleBreakpoints.reduce((activeIndex, breakpoint, index) => {
      return quantity >= Number(breakpoint.quantity) ? index : activeIndex;
    }, -1);

    this.nodesWrap.querySelectorAll('.milestone-node').forEach((node, index) => {
      const breakpoint = visibleBreakpoints[index];
      const breakpointQty = Number(breakpoint.quantity);
      const discount = Number(breakpoint.discount_percentage) || 0;
      const unitPrice = basePrice ? (basePrice * (1 - discount / 100)) / pouchCountPerUnit : 0;
      const totalPouches = breakpointQty * pouchCountPerUnit;

      node.dataset.quantity = breakpointQty;
      node.dataset.discount = discount;
      node.classList.toggle('active-gold', index === activeTierIndex);
      node.classList.toggle('is-active-tier', index === activeTierIndex);
      node.classList.toggle('is-current', quantity === breakpointQty);
      node.classList.toggle('is-passed', quantity > breakpointQty);
      node.classList.toggle('is-future', quantity < breakpointQty);

      const quantityLabel = this.getQuantityLabel(breakpointQty, packLabel);
      const button = node.querySelector('[data-milestone-quantity-button]');
      button.dataset.milestoneQuantity = breakpointQty;
      button.setAttribute('aria-label', `Set quantity to ${breakpointQty} ${quantityLabel.toLowerCase()}`);

      node.querySelector('.node-circle-number').textContent = breakpointQty;
      node.querySelector('.node-circle-label').textContent = this.toTitleCase(quantityLabel);
      node.querySelector('.node-qty').textContent = `${breakpointQty} ${quantityLabel.toLowerCase()}`;
      node.querySelector('.node-pill').textContent = discount > 0 ? `Save ${discount}%` : '';
      node.querySelector('.node-pill').hidden = discount <= 0;
      node.querySelector('.node-pouch-price').textContent = unitPrice ? `$${unitPrice.toFixed(2)} / Pouch` : '';
      node.querySelector('.node-helper').textContent = quantity >= breakpointQty ? 'Selected' : `Add ${breakpointQty - quantity} more`;

      if (totalPouches > breakpointQty) {
        node.querySelector('.node-qty').setAttribute('title', `${totalPouches} pouches total`);
      }
    });
  }

  getQuantityLabel(quantity, packLabel) {
    const cleanLabel = String(packLabel || 'units').toLowerCase();
    const singularMap = {
      pouches: 'pouch',
      pouch: 'pouch',
      boxes: 'box',
      box: 'box',
      units: 'unit',
      unit: 'unit'
    };
    const singular = singularMap[cleanLabel] || cleanLabel.replace(/s$/, '');
    return quantity === 1 ? singular.toUpperCase() : `${singular}S`.toUpperCase();
  }

  toTitleCase(value) {
    const lower = String(value || '').toLowerCase();
    return lower ? lower.charAt(0).toUpperCase() + lower.slice(1) : '';
  }

  hide() {
    this.container.hidden = true;
  }

  show() {
    this.container.hidden = false;
  }
}

function initSavingsMilestones() {
  document.querySelectorAll('.product-savings-milestones').forEach((container) => {
    if (!container.dataset.savingsMilestonesInitialized) {
      container.dataset.savingsMilestonesInitialized = 'true';
      new ProductSavingsMilestones(container);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSavingsMilestones);
} else {
  initSavingsMilestones();
}

document.addEventListener('shopify:section:load', initSavingsMilestones);
